import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StarDecorations } from "components/ui/StarDecorations";
import { CardIniciativaELBB } from "components/ui/CardIniciativaELBB";
import { CardIniciativaConexao } from "components/ui/CardIniciativaConexao";
import { buscarEntregas, invalidarCacheEntregas } from "services/entrega.service";
import { useAuth } from "context/AuthContext";
import { FiCalendar } from "react-icons/fi";
import { PHASE_IDS } from "utils/constants";
import { useActivePhases, useControlDate } from "hooks/useControlDate";
import { DateRangeDisplay } from "components/ui/DateRangeDisplay";

export function Listagem() {
  const LIMITE_CADASTROS = 1;
  
  const [entregas, setEntregas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const { user } = useAuth();
  const navigate = useNavigate();
  const phases = useActivePhases();

  // TODO: Deixar dinâmico quando separar usuários por categoria (ELBB vs Conexão)
  const categoriaPredominate = "ELBB";

  // Filtra entregas pela categoria predominante
  const entregasFiltradas = entregas.filter((e) => e.categoryPremio === categoriaPredominate);

  // Separa iniciativas próprias das que o usuário participa como membro da equipe
  const minhasIniciativas = entregasFiltradas.filter((e) => e.owner === user?._id);
  const participando = entregasFiltradas.filter(
    (e) => e.owner !== user?._id && e.team?.some((m: any) => m._id === user?._id)
  );

  // TODO: Usar período dinâmico conforme categoriaPredominate
  const periodoCadastro = useControlDate(PHASE_IDS.ELBB_PRE_REGISTRATION);

  // TODO: Usar rota dinâmica conforme categoriaPredominate
  const rotaCadastro = phases.canAccessCadastroELBB ? "/cadastroELBB" : null;

  const CardComponent = categoriaPredominate === "ELBB"
    ? CardIniciativaELBB
    : CardIniciativaConexao;
    
  const carregarEntregas = async () => {
    try {
      setLoading(true);
      const params = {
        player: user?._id || "",
        start: "2026-01-01T00:00:00-03:00",
      };
      invalidarCacheEntregas(params);
      const data = await buscarEntregas(params);
      const result = data || [];
      setEntregas(result);
      setExpandedCards(new Set(result.map((_: any, i: number) => i)));
    } catch (error) {
      setEntregas([]);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (user?._id) {
      carregarEntregas();
    }
  }, [user]);

  const toggleCard = (index: number) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-bb via-blue-bb-1 to-blue-bb-5 overflow-x-hidden relative">
      {/* Star Decorations - Hidden on Mobile */}
      <div aria-hidden="true" className="hidden md:block pointer-events-none">
        <StarDecorations count={18} density="medium" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-12 max-w-6xl">
        <header className="mb-6 sm:mb-7 lg:mb-8">
          <h1 className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 leading-tight">
            Acompanhe a validação da sua iniciativa
          </h1>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-2 text-yellow-400 text-sm sm:text-base">
            <FiCalendar className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span className="font-semibold">
              {periodoCadastro ? (
                <DateRangeDisplay
                  dataInicio={periodoCadastro.dataInicio}
                  dataFim={periodoCadastro.dataFim}
                  dateFormat="dd/MM"
                  prefix="PERÍODO PARA CADASTRO: "
                  uppercase={false}
                  className="font-semibold"
                />) : "Período de cadastro não disponível"}
            </span>
          </div>
        </header>

        <main className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-xl sm:shadow-2xl">
          {loading ? (
            <p className="text-gray-600 text-center text-base sm:text-lg py-8">
              Carregando iniciativas...
            </p>
          ) : (
            <>
              {/* Seção: Iniciativas inscritas (owner) */}
              <div className="mb-8 sm:mb-10">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-5 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                    Iniciativas inscritas:
                  </h2>
                  {rotaCadastro && minhasIniciativas.length < LIMITE_CADASTROS && (
                    <button
                      onClick={() => navigate(rotaCadastro)}
                      className="w-full sm:w-auto text-blue-600 font-semibold hover:text-blue-700 active:text-blue-800 transition-colors text-sm sm:text-base py-2 sm:py-0 px-4 sm:px-0 bg-blue-50 sm:bg-transparent rounded-lg sm:rounded-none touch-manipulation"
                    >
                      + ADICIONAR INICIATIVA
                    </button>
                  )}
                </div>

                {minhasIniciativas.length === 0 ? (
                  <p className="text-gray-500 text-sm sm:text-base">
                    Você ainda não cadastrou nenhuma iniciativa.
                  </p>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {minhasIniciativas.map((entrega, index) => (
                      <CardComponent
                        key={entrega._id || index}
                        entrega={entrega}
                        index={index}
                        isExpanded={expandedCards.has(index)}
                        onToggle={() => toggleCard(index)}
                        onUpdate={carregarEntregas}
                        currentUserId={user?._id}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Seção: Iniciativas em que participo como membro da equipe */}
              {participando.length > 0 && (
                <div>
                  <div className="mb-5 sm:mb-6">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                      Iniciativas que estou participando:
                    </h2>
                  </div>
                  <div className="space-y-3 sm:space-y-4">
                    {participando.map((entrega, index) => {
                      const globalIndex = minhasIniciativas.length + index;
                      return (
                        <CardComponent
                          key={entrega._id || globalIndex}
                          entrega={entrega}
                          index={globalIndex}
                          isExpanded={expandedCards.has(globalIndex)}
                          onToggle={() => toggleCard(globalIndex)}
                          onUpdate={carregarEntregas}
                          currentUserId={user?._id}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
